import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import axios from 'axios';
import { toast } from 'sonner';
import { API } from '../App';
import Navbar from '../components/Navbar';
import Footer from '../components/Footer';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';
import { Switch } from '@/components/ui/switch';
import { Textarea } from '@/components/ui/textarea';
import { Checkbox } from '@/components/ui/checkbox';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from "@/components/ui/dialog";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { 
  ChevronLeft, Loader2, Building2, Users, Search, Plus,
  Trash2, Edit, DollarSign, Package, Globe, FileText,
  UserPlus, Clock, Eye, ChevronRight, Shield, History
} from 'lucide-react';

const AdminCompanies = () => {
  const [companies, setCompanies] = useState([]);
  const [suggestions, setSuggestions] = useState([]);
  const [allUsers, setAllUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  
  // Dialogs
  const [showCreateDialog, setShowCreateDialog] = useState(false);
  const [showEditDialog, setShowEditDialog] = useState(false);
  const [showViewDialog, setShowViewDialog] = useState(false);
  const [showAddMemberDialog, setShowAddMemberDialog] = useState(false);
  const [showSuggestionDialog, setShowSuggestionDialog] = useState(false);
  const [showAuditDialog, setShowAuditDialog] = useState(false);
  
  const [selectedCompany, setSelectedCompany] = useState(null);
  const [selectedSuggestion, setSelectedSuggestion] = useState(null);
  const [auditLogs, setAuditLogs] = useState([]);
  const [saving, setSaving] = useState(false);
  
  const [newCompany, setNewCompany] = useState({
    name: '',
    abn: '',
    domain: '',
    invoice_enabled: false,
    notes: ''
  });
  
  const [editCompany, setEditCompany] = useState({
    name: '',
    abn: '',
    domain: '',
    invoice_enabled: false,
    notes: ''
  });
  
  const [newMember, setNewMember] = useState({
    user_id: '',
    role: 'viewer'
  });
  
  const [suggestionData, setSuggestionData] = useState({
    name: '',
    selectedUsers: []
  });

  const fetchData = async () => {
    try {
      const [companiesRes, suggestionsRes, usersRes] = await Promise.all([
        axios.get(`${API}/admin/companies`, {
          headers: { Authorization: `Bearer ${localStorage.getItem('auth_token')}` }
        }),
        axios.get(`${API}/admin/companies/suggestions`, {
          headers: { Authorization: `Bearer ${localStorage.getItem('auth_token')}` }
        }),
        axios.get(`${API}/admin/users`, {
          headers: { Authorization: `Bearer ${localStorage.getItem('auth_token')}` }
        })
      ]);
      setCompanies(companiesRes.data.companies || []);
      setSuggestions(suggestionsRes.data.suggestions || []);
      setAllUsers(usersRes.data.users?.filter(u => u.role === 'customer') || []);
    } catch (error) {
      toast.error('Failed to fetch data');
    }
    setLoading(false);
  };

  useEffect(() => {
    fetchData();
  }, []);

  const handleCreateCompany = async () => {
    if (!newCompany.name.trim()) {
      toast.error('Company name is required');
      return;
    }
    setSaving(true);
    try {
      await axios.post(`${API}/admin/companies`, newCompany, {
        headers: { Authorization: `Bearer ${localStorage.getItem('auth_token')}` }
      });
      toast.success('Company created successfully');
      setShowCreateDialog(false);
      setNewCompany({ name: '', abn: '', domain: '', invoice_enabled: false, notes: '' });
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to create company');
    }
    setSaving(false);
  };

  const handleUpdateCompany = async () => {
    if (!selectedCompany) return;
    setSaving(true);
    try {
      await axios.put(`${API}/admin/companies/${selectedCompany.company_id}`, editCompany, {
        headers: { Authorization: `Bearer ${localStorage.getItem('auth_token')}` }
      });
      toast.success('Company updated successfully');
      setShowEditDialog(false);
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to update company');
    }
    setSaving(false);
  };

  const handleDeleteCompany = async (companyId) => {
    if (!window.confirm('Are you sure you want to delete this company? All member associations will be removed.')) {
      return;
    }
    try {
      await axios.delete(`${API}/admin/companies/${companyId}`, {
        headers: { Authorization: `Bearer ${localStorage.getItem('auth_token')}` }
      });
      toast.success('Company deleted');
      setShowViewDialog(false);
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to delete company');
    }
  };

  const handleViewCompany = async (company) => {
    try {
      const response = await axios.get(`${API}/admin/companies/${company.company_id}`, {
        headers: { Authorization: `Bearer ${localStorage.getItem('auth_token')}` }
      });
      setSelectedCompany(response.data);
      setShowViewDialog(true);
    } catch (error) {
      toast.error('Failed to load company details');
    }
  };

  const handleEditCompany = (company) => {
    setSelectedCompany(company);
    setEditCompany({
      name: company.name || '',
      abn: company.abn || '',
      domain: company.domain || '',
      invoice_enabled: company.invoice_enabled || false,
      notes: company.notes || ''
    });
    setShowEditDialog(true);
  };

  const handleAddMember = async () => {
    if (!newMember.user_id) {
      toast.error('Please select a user');
      return;
    }
    setSaving(true);
    try {
      await axios.post(`${API}/admin/companies/${selectedCompany.company_id}/members`, newMember, {
        headers: { Authorization: `Bearer ${localStorage.getItem('auth_token')}` }
      });
      toast.success('Member added successfully');
      setShowAddMemberDialog(false);
      setNewMember({ user_id: '', role: 'viewer' });
      // Refresh company details
      handleViewCompany(selectedCompany);
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to add member');
    }
    setSaving(false);
  };

  const handleUpdateMemberRole = async (userId, newRole) => {
    try {
      await axios.put(`${API}/admin/companies/${selectedCompany.company_id}/members/${userId}`, 
        { role: newRole },
        { headers: { Authorization: `Bearer ${localStorage.getItem('auth_token')}` }}
      );
      toast.success('Member role updated');
      handleViewCompany(selectedCompany);
    } catch (error) {
      toast.error('Failed to update member role');
    }
  };

  const handleRemoveMember = async (userId) => {
    if (!window.confirm('Remove this member from the company?')) return;
    try {
      await axios.delete(`${API}/admin/companies/${selectedCompany.company_id}/members/${userId}`, {
        headers: { Authorization: `Bearer ${localStorage.getItem('auth_token')}` }
      });
      toast.success('Member removed');
      handleViewCompany(selectedCompany);
      fetchData();
    } catch (error) {
      toast.error('Failed to remove member');
    }
  };

  const handleOpenSuggestion = (suggestion) => {
    setSelectedSuggestion(suggestion);
    setSuggestionData({
      name: suggestion.domain.split('.')[0].charAt(0).toUpperCase() + suggestion.domain.split('.')[0].slice(1),
      selectedUsers: suggestion.users.map(u => u.user_id)
    });
    setShowSuggestionDialog(true);
  };

  const handleCreateFromSuggestion = async () => {
    if (!suggestionData.name.trim()) {
      toast.error('Company name is required');
      return;
    }
    setSaving(true);
    try {
      await axios.post(`${API}/admin/companies/from-suggestion`, {
        domain: selectedSuggestion.domain,
        name: suggestionData.name,
        user_ids: suggestionData.selectedUsers,
        invoice_enabled: false
      }, {
        headers: { Authorization: `Bearer ${localStorage.getItem('auth_token')}` }
      });
      toast.success('Company created from suggestion');
      setShowSuggestionDialog(false);
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to create company');
    }
    setSaving(false);
  };

  const toggleSuggestionUser = (userId) => {
    setSuggestionData(prev => ({
      ...prev,
      selectedUsers: prev.selectedUsers.includes(userId)
        ? prev.selectedUsers.filter(id => id !== userId)
        : [...prev.selectedUsers, userId]
    }));
  };

  const handleViewAudit = async (company) => {
    try {
      const response = await axios.get(`${API}/admin/companies/${company.company_id}/audit`, {
        headers: { Authorization: `Bearer ${localStorage.getItem('auth_token')}` }
      });
      setSelectedCompany(company);
      setAuditLogs(response.data.audit_logs || []);
      setShowAuditDialog(true);
    } catch (error) {
      toast.error('Failed to load audit log');
    }
  };

  const filteredCompanies = companies.filter(c => 
    c.name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
    c.domain?.toLowerCase().includes(searchTerm.toLowerCase())
  );

  // Get available users (not already in a company)
  const availableUsers = allUsers.filter(user => {
    // Check if user is already in a company
    for (const company of companies) {
      if (company.members?.some(m => m.user_id === user.user_id)) {
        return false;
      }
    }
    // Also check if user has company_id set
    if (user.company_id) return false;
    return true;
  });

  const getActionLabel = (action) => {
    switch (action) {
      case 'viewed_company_orders': return 'Viewed Company Orders';
      case 'viewed_order': return 'Viewed Order';
      case 'reordered': return 'Created Reorder';
      case 'accepted_quote': return 'Accepted Quote';
      case 'downloaded_file': return 'Downloaded File';
      default: return action;
    }
  };

  return (
    <div className="min-h-screen bg-[#050505]" data-testid="admin-companies">
      <Navbar />
      
      <main className="pt-32 pb-16">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          {/* Header */}
          <div className="flex flex-col md:flex-row md:items-center md:justify-between mb-8">
            <div className="flex items-center gap-4">
              <Link to="/dashboard">
                <Button variant="outline" size="icon" className="border-white/20 text-white hover:bg-white/5 rounded-none">
                  <ChevronLeft className="w-4 h-4" />
                </Button>
              </Link>
              <div>
                <span className="overline text-[#CCFF00]">Management</span>
                <h1 className="text-2xl font-['Chivo'] font-black text-white">Companies</h1>
              </div>
            </div>
            <Button
              onClick={() => setShowCreateDialog(true)}
              className="bg-[#CCFF00] text-black hover:bg-[#B3E600] font-bold uppercase tracking-wide rounded-none mt-4 md:mt-0"
            >
              <Plus className="w-4 h-4 mr-2" />
              Create Company
            </Button>
          </div>

          {/* Suggestions Banner */}
          {suggestions.length > 0 && (
            <div className="bg-yellow-500/10 border border-yellow-500/30 rounded-sm p-4 mb-6">
              <div className="flex items-center gap-3">
                <Globe className="w-6 h-6 text-yellow-500" />
                <div className="flex-1">
                  <h3 className="text-yellow-500 font-bold">Suggested Company Groupings</h3>
                  <p className="text-white/50 text-sm">
                    We detected {suggestions.length} potential companies based on email domains
                  </p>
                </div>
              </div>
              <div className="flex flex-wrap gap-2 mt-3">
                {suggestions.slice(0, 5).map((suggestion) => (
                  <Button
                    key={suggestion.domain}
                    variant="outline"
                    size="sm"
                    onClick={() => handleOpenSuggestion(suggestion)}
                    className="border-yellow-500/30 text-yellow-500 hover:bg-yellow-500/10 rounded-none"
                  >
                    @{suggestion.domain} ({suggestion.user_count} users)
                  </Button>
                ))}
                {suggestions.length > 5 && (
                  <span className="text-white/30 text-sm self-center">+{suggestions.length - 5} more</span>
                )}
              </div>
            </div>
          )}

          {/* Search */}
          <div className="bg-[#111] border border-white/10 rounded-sm p-4 mb-6">
            <div className="relative">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-white/30" />
              <Input
                placeholder="Search companies by name or domain..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="bg-white/5 border-white/10 text-white pl-10"
              />
            </div>
          </div>

          {/* Companies List */}
          <div className="bg-[#111] border border-white/10 rounded-sm">
            {loading ? (
              <div className="p-12 text-center">
                <Loader2 className="w-8 h-8 text-[#CCFF00] mx-auto animate-spin" />
              </div>
            ) : filteredCompanies.length === 0 ? (
              <div className="p-12 text-center">
                <Building2 className="w-12 h-12 text-white/20 mx-auto mb-4" />
                <p className="text-white/50 mb-4">No companies yet</p>
                <Button
                  onClick={() => setShowCreateDialog(true)}
                  className="bg-[#CCFF00] text-black hover:bg-[#B3E600] rounded-none"
                >
                  <Plus className="w-4 h-4 mr-2" />
                  Create First Company
                </Button>
              </div>
            ) : (
              <div className="divide-y divide-white/10">
                {filteredCompanies.map((company) => (
                  <div 
                    key={company.company_id}
                    className="p-4 hover:bg-white/5 transition-colors cursor-pointer"
                    onClick={() => handleViewCompany(company)}
                  >
                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-4">
                        <div className="w-12 h-12 bg-[#CCFF00]/10 rounded-sm flex items-center justify-center">
                          <Building2 className="w-6 h-6 text-[#CCFF00]" />
                        </div>
                        <div>
                          <div className="flex items-center gap-2">
                            <h3 className="text-white font-bold">{company.name}</h3>
                            {company.invoice_enabled && (
                              <Badge className="bg-green-500/10 text-green-400 border-green-500/20">
                                Invoice Enabled
                              </Badge>
                            )}
                          </div>
                          <div className="flex items-center gap-3 text-white/50 text-sm mt-1">
                            {company.domain && (
                              <span className="flex items-center gap-1">
                                <Globe className="w-3 h-3" />
                                @{company.domain}
                              </span>
                            )}
                            {company.abn && (
                              <span>ABN: {company.abn}</span>
                            )}
                          </div>
                        </div>
                      </div>
                      <div className="flex items-center gap-6">
                        <div className="text-center">
                          <p className="text-2xl font-bold text-white font-mono">{company.member_count || 0}</p>
                          <p className="text-white/30 text-xs">Members</p>
                        </div>
                        <div className="text-center">
                          <p className="text-2xl font-bold text-[#CCFF00] font-mono">{company.order_count || 0}</p>
                          <p className="text-white/30 text-xs">Orders</p>
                        </div>
                        <div className="text-center">
                          <p className="text-2xl font-bold text-green-500 font-mono">${(company.total_spent || 0).toFixed(0)}</p>
                          <p className="text-white/30 text-xs">Spent</p>
                        </div>
                        <ChevronRight className="w-5 h-5 text-white/30" />
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>
      </main>

      {/* Create Company Dialog */}
      <Dialog open={showCreateDialog} onOpenChange={setShowCreateDialog}>
        <DialogContent className="bg-[#111] border-white/10 text-white max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Building2 className="w-5 h-5 text-[#CCFF00]" />
              Create Company
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div>
              <Label className="text-white/70">Company Name *</Label>
              <Input
                value={newCompany.name}
                onChange={(e) => setNewCompany(prev => ({ ...prev, name: e.target.value }))}
                className="bg-white/5 border-white/10 text-white mt-2"
                placeholder="Acme Corporation"
              />
            </div>
            <div>
              <Label className="text-white/70">ABN</Label>
              <Input
                value={newCompany.abn}
                onChange={(e) => setNewCompany(prev => ({ ...prev, abn: e.target.value }))}
                className="bg-white/5 border-white/10 text-white mt-2"
                placeholder="12 345 678 901"
              />
            </div>
            <div>
              <Label className="text-white/70">Email Domain</Label>
              <Input
                value={newCompany.domain}
                onChange={(e) => setNewCompany(prev => ({ ...prev, domain: e.target.value }))}
                className="bg-white/5 border-white/10 text-white mt-2"
                placeholder="acme.com.au"
              />
              <p className="text-white/30 text-xs mt-1">Users with this domain will be suggested for this company</p>
            </div>
            <div className="flex items-center justify-between bg-white/5 p-3 rounded-sm">
              <div>
                <Label className="text-white">Pay on Invoice</Label>
                <p className="text-white/30 text-xs">All members can use invoice payment</p>
              </div>
              <Switch
                checked={newCompany.invoice_enabled}
                onCheckedChange={(checked) => setNewCompany(prev => ({ ...prev, invoice_enabled: checked }))}
                className="data-[state=checked]:bg-[#CCFF00]"
              />
            </div>
            <div>
              <Label className="text-white/70">Notes</Label>
              <Textarea
                value={newCompany.notes}
                onChange={(e) => setNewCompany(prev => ({ ...prev, notes: e.target.value }))}
                className="bg-white/5 border-white/10 text-white mt-2"
                placeholder="Internal notes..."
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowCreateDialog(false)} className="border-white/20 text-white">
              Cancel
            </Button>
            <Button onClick={handleCreateCompany} disabled={saving} className="bg-[#CCFF00] text-black hover:bg-[#B3E600]">
              {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : 'Create Company'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Edit Company Dialog */}
      <Dialog open={showEditDialog} onOpenChange={setShowEditDialog}>
        <DialogContent className="bg-[#111] border-white/10 text-white max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Edit className="w-5 h-5 text-[#CCFF00]" />
              Edit Company
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div>
              <Label className="text-white/70">Company Name *</Label>
              <Input
                value={editCompany.name}
                onChange={(e) => setEditCompany(prev => ({ ...prev, name: e.target.value }))}
                className="bg-white/5 border-white/10 text-white mt-2"
              />
            </div>
            <div>
              <Label className="text-white/70">ABN</Label>
              <Input
                value={editCompany.abn}
                onChange={(e) => setEditCompany(prev => ({ ...prev, abn: e.target.value }))}
                className="bg-white/5 border-white/10 text-white mt-2"
              />
            </div>
            <div>
              <Label className="text-white/70">Email Domain</Label>
              <Input
                value={editCompany.domain}
                onChange={(e) => setEditCompany(prev => ({ ...prev, domain: e.target.value }))}
                className="bg-white/5 border-white/10 text-white mt-2"
              />
            </div>
            <div className="flex items-center justify-between bg-white/5 p-3 rounded-sm">
              <div>
                <Label className="text-white">Pay on Invoice</Label>
                <p className="text-white/30 text-xs">All members can use invoice payment</p>
              </div>
              <Switch
                checked={editCompany.invoice_enabled}
                onCheckedChange={(checked) => setEditCompany(prev => ({ ...prev, invoice_enabled: checked }))}
                className="data-[state=checked]:bg-[#CCFF00]"
              />
            </div>
            <div>
              <Label className="text-white/70">Notes</Label>
              <Textarea
                value={editCompany.notes}
                onChange={(e) => setEditCompany(prev => ({ ...prev, notes: e.target.value }))}
                className="bg-white/5 border-white/10 text-white mt-2"
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowEditDialog(false)} className="border-white/20 text-white">
              Cancel
            </Button>
            <Button onClick={handleUpdateCompany} disabled={saving} className="bg-[#CCFF00] text-black hover:bg-[#B3E600]">
              {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : 'Save Changes'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* View Company Dialog */}
      <Dialog open={showViewDialog} onOpenChange={setShowViewDialog}>
        <DialogContent className="bg-[#111] border-white/10 text-white max-w-2xl max-h-[85vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="flex items-center justify-between">
              <div className="flex items-center gap-3">
                <Building2 className="w-5 h-5 text-[#CCFF00]" />
                {selectedCompany?.name}
              </div>
              <div className="flex items-center gap-2">
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={() => handleViewAudit(selectedCompany)}
                  className="text-white/50 hover:text-white"
                >
                  <History className="w-4 h-4 mr-1" />
                  Audit Log
                </Button>
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={() => handleEditCompany(selectedCompany)}
                  className="text-[#CCFF00] hover:text-[#CCFF00]"
                >
                  <Edit className="w-4 h-4" />
                </Button>
              </div>
            </DialogTitle>
          </DialogHeader>
          
          {selectedCompany && (
            <div className="space-y-6 py-4">
              {/* Company Info */}
              <div className="bg-white/5 rounded-sm p-4">
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <p className="text-white/50 text-sm">Domain</p>
                    <p className="text-white">{selectedCompany.domain ? `@${selectedCompany.domain}` : 'Not set'}</p>
                  </div>
                  <div>
                    <p className="text-white/50 text-sm">ABN</p>
                    <p className="text-white">{selectedCompany.abn || 'Not set'}</p>
                  </div>
                  <div>
                    <p className="text-white/50 text-sm">Invoice Payment</p>
                    <Badge className={selectedCompany.invoice_enabled ? 'bg-green-500/10 text-green-400' : 'bg-white/10 text-white/50'}>
                      {selectedCompany.invoice_enabled ? 'Enabled' : 'Disabled'}
                    </Badge>
                  </div>
                  <div>
                    <p className="text-white/50 text-sm">Created</p>
                    <p className="text-white">{new Date(selectedCompany.created_at).toLocaleDateString('en-AU')}</p>
                  </div>
                </div>
                {selectedCompany.notes && (
                  <div className="mt-4 pt-4 border-t border-white/10">
                    <p className="text-white/50 text-sm">Notes</p>
                    <p className="text-white/70 text-sm mt-1">{selectedCompany.notes}</p>
                  </div>
                )}
              </div>

              {/* Members */}
              <div>
                <div className="flex items-center justify-between mb-3">
                  <h3 className="text-white font-bold flex items-center gap-2">
                    <Users className="w-4 h-4 text-[#CCFF00]" />
                    Members ({selectedCompany.members?.length || 0})
                  </h3>
                  <Button
                    size="sm"
                    onClick={() => setShowAddMemberDialog(true)}
                    className="bg-[#CCFF00] text-black hover:bg-[#B3E600] rounded-none"
                  >
                    <UserPlus className="w-4 h-4 mr-1" />
                    Add Member
                  </Button>
                </div>
                
                <div className="bg-white/5 rounded-sm divide-y divide-white/10">
                  {selectedCompany.members?.length === 0 ? (
                    <div className="p-6 text-center">
                      <Users className="w-8 h-8 text-white/20 mx-auto mb-2" />
                      <p className="text-white/50 text-sm">No members yet</p>
                    </div>
                  ) : (
                    selectedCompany.members?.map((member) => (
                      <div key={member.user_id} className="p-3 flex items-center justify-between">
                        <div className="flex items-center gap-3">
                          <div className="w-8 h-8 rounded-full bg-[#CCFF00]/10 flex items-center justify-center">
                            <span className="text-[#CCFF00] text-sm font-bold">
                              {member.user?.name?.charAt(0)?.toUpperCase() || '?'}
                            </span>
                          </div>
                          <div>
                            <p className="text-white font-medium">{member.user?.name || 'Unknown'}</p>
                            <p className="text-white/50 text-xs">{member.user?.email}</p>
                          </div>
                        </div>
                        <div className="flex items-center gap-2">
                          <Select 
                            value={member.role} 
                            onValueChange={(v) => handleUpdateMemberRole(member.user_id, v)}
                          >
                            <SelectTrigger className="w-[100px] bg-white/5 border-white/10 text-white h-8 text-sm">
                              <SelectValue />
                            </SelectTrigger>
                            <SelectContent className="bg-[#111] border-white/10">
                              <SelectItem value="viewer" className="text-white">Viewer</SelectItem>
                              <SelectItem value="manager" className="text-white">Manager</SelectItem>
                            </SelectContent>
                          </Select>
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => handleRemoveMember(member.user_id)}
                            className="text-red-400 hover:text-red-400 hover:bg-red-400/10 h-8 w-8 p-0"
                          >
                            <Trash2 className="w-4 h-4" />
                          </Button>
                        </div>
                      </div>
                    ))
                  )}
                </div>
                
                <p className="text-white/30 text-xs mt-2">
                  <Shield className="w-3 h-3 inline mr-1" />
                  Managers can accept quotes and reorder on behalf of the company
                </p>
              </div>

              {/* Actions */}
              <div className="flex gap-3 pt-4 border-t border-white/10">
                <Link to={`/admin/orders?company=${selectedCompany.company_id}`} className="flex-1">
                  <Button variant="outline" className="w-full border-white/20 text-white hover:bg-white/5 rounded-none">
                    <Package className="w-4 h-4 mr-2" />
                    View Orders
                  </Button>
                </Link>
                <Link to={`/admin/quote-requests?company=${selectedCompany.company_id}`} className="flex-1">
                  <Button variant="outline" className="w-full border-white/20 text-white hover:bg-white/5 rounded-none">
                    <FileText className="w-4 h-4 mr-2" />
                    View Quotes
                  </Button>
                </Link>
                <Button
                  variant="outline"
                  onClick={() => handleDeleteCompany(selectedCompany.company_id)}
                  className="border-red-400/50 text-red-400 hover:bg-red-400/10 rounded-none"
                >
                  <Trash2 className="w-4 h-4 mr-2" />
                  Delete
                </Button>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* Add Member Dialog */}
      <Dialog open={showAddMemberDialog} onOpenChange={setShowAddMemberDialog}>
        <DialogContent className="bg-[#111] border-white/10 text-white max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <UserPlus className="w-5 h-5 text-[#CCFF00]" />
              Add Member to {selectedCompany?.name}
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div>
              <Label className="text-white/70">Select User *</Label>
              <Select value={newMember.user_id} onValueChange={(v) => setNewMember(prev => ({ ...prev, user_id: v }))}>
                <SelectTrigger className="bg-white/5 border-white/10 text-white mt-2">
                  <SelectValue placeholder="Choose a user..." />
                </SelectTrigger>
                <SelectContent className="bg-[#111] border-white/10 max-h-[200px]">
                  {availableUsers.length === 0 ? (
                    <div className="p-3 text-white/50 text-center text-sm">No available users</div>
                  ) : (
                    availableUsers.map((user) => (
                      <SelectItem key={user.user_id} value={user.user_id} className="text-white">
                        {user.name} ({user.email})
                      </SelectItem>
                    ))
                  )}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label className="text-white/70">Role</Label>
              <Select value={newMember.role} onValueChange={(v) => setNewMember(prev => ({ ...prev, role: v }))}>
                <SelectTrigger className="bg-white/5 border-white/10 text-white mt-2">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent className="bg-[#111] border-white/10">
                  <SelectItem value="viewer" className="text-white">Viewer - Can view company orders</SelectItem>
                  <SelectItem value="manager" className="text-white">Manager - Can accept quotes & reorder</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowAddMemberDialog(false)} className="border-white/20 text-white">
              Cancel
            </Button>
            <Button onClick={handleAddMember} disabled={saving} className="bg-[#CCFF00] text-black hover:bg-[#B3E600]">
              {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : 'Add Member'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Create from Suggestion Dialog */}
      <Dialog open={showSuggestionDialog} onOpenChange={setShowSuggestionDialog}>
        <DialogContent className="bg-[#111] border-white/10 text-white max-w-lg max-h-[85vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Globe className="w-5 h-5 text-yellow-500" />
              Create Company from @{selectedSuggestion?.domain}
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div>
              <Label className="text-white/70">Company Name *</Label>
              <Input
                value={suggestionData.name}
                onChange={(e) => setSuggestionData(prev => ({ ...prev, name: e.target.value }))}
                className="bg-white/5 border-white/10 text-white mt-2"
                placeholder="Enter company name"
              />
            </div>
            
            <div>
              <Label className="text-white/70">Select Users to Add ({suggestionData.selectedUsers.length} selected)</Label>
              <div className="bg-white/5 rounded-sm mt-2 max-h-[250px] overflow-y-auto">
                {selectedSuggestion?.users?.map((user) => (
                  <div 
                    key={user.user_id}
                    className="p-3 flex items-center gap-3 border-b border-white/10 last:border-0 cursor-pointer hover:bg-white/5"
                    onClick={() => toggleSuggestionUser(user.user_id)}
                  >
                    <Checkbox 
                      checked={suggestionData.selectedUsers.includes(user.user_id)}
                      className="data-[state=checked]:bg-[#CCFF00] data-[state=checked]:border-[#CCFF00]"
                    />
                    <div className="flex-1">
                      <p className="text-white font-medium">{user.name || 'Unnamed'}</p>
                      <p className="text-white/50 text-xs">{user.email}</p>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowSuggestionDialog(false)} className="border-white/20 text-white">
              Cancel
            </Button>
            <Button onClick={handleCreateFromSuggestion} disabled={saving} className="bg-[#CCFF00] text-black hover:bg-[#B3E600]">
              {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : 'Create Company'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Audit Log Dialog */}
      <Dialog open={showAuditDialog} onOpenChange={setShowAuditDialog}>
        <DialogContent className="bg-[#111] border-white/10 text-white max-w-2xl max-h-[85vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <History className="w-5 h-5 text-[#CCFF00]" />
              Audit Log - {selectedCompany?.name}
            </DialogTitle>
          </DialogHeader>
          <div className="py-4">
            {auditLogs.length === 0 ? (
              <div className="p-8 text-center">
                <History className="w-10 h-10 text-white/20 mx-auto mb-3" />
                <p className="text-white/50">No activity recorded yet</p>
              </div>
            ) : (
              <div className="space-y-2">
                {auditLogs.map((log) => (
                  <div key={log.log_id} className="bg-white/5 p-3 rounded-sm">
                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-2">
                        <Badge variant="outline" className="text-white/70 border-white/20 text-xs">
                          {getActionLabel(log.action)}
                        </Badge>
                        {log.target_order_id && (
                          <span className="text-[#CCFF00] font-mono text-sm">{log.target_order_id}</span>
                        )}
                        {log.target_quote_id && (
                          <span className="text-yellow-500 font-mono text-sm">{log.target_quote_id}</span>
                        )}
                      </div>
                      <span className="text-white/30 text-xs">
                        {new Date(log.timestamp).toLocaleString('en-AU')}
                      </span>
                    </div>
                    <div className="mt-1">
                      <span className="text-white font-medium">{log.user_name}</span>
                      <span className="text-white/50 text-sm ml-2">({log.user_email})</span>
                    </div>
                    {log.details && (
                      <p className="text-white/50 text-xs mt-1">{log.details}</p>
                    )}
                  </div>
                ))}
              </div>
            )}
          </div>
        </DialogContent>
      </Dialog>

      <Footer />
    </div>
  );
};

export default AdminCompanies;
